import { useState, useEffect, useRef, lazy, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import "./css/Layout.css";
import "./css/Master.css";
import "./css/MasterHub.css";
import api from "./api/api";
import logo from "./Images/logo.png";
import {
  FiMenu, FiX, FiArrowLeft, FiChevronDown, FiChevronRight,
  FiSearch, FiClock, FiArrowRight, FiBell, FiHelpCircle, FiLogOut,
  FiHome, FiTruck, FiUsers, FiTag, FiLayers, FiBox, FiSliders,
  FiDroplet, FiGrid, FiPackage, FiShoppingCart, FiTrendingUp,
  FiCreditCard, FiFileText, FiBarChart2, FiPieChart,
  FiDatabase, FiShoppingBag,
} from "react-icons/fi";

/* ═══ Helpers ═══════════════════════════════════════════════════════════ */

const GROUPS = [
  { key:"core",         label:"Core masters",  match:["company","supplier","customer"] },
  { key:"product",      label:"Product setup",  match:["category","subcategory","product","variant","size","colour","color","stock"] },
  { key:"transactions", label:"Transactions",   match:["purchase","sales","payment"] },
];
const GROUP_ORDER  = ["core","product","transactions","other"];
const GROUP_LABELS = { core:"Core masters", product:"Product setup", transactions:"Transactions", other:"Other" };

function getGroupKey(name=""){
  const n=name.toLowerCase().replace(/\s+/g,"");
  for(const g of GROUPS) if(g.match.some(k=>n.includes(k))) return g.key;
  return "other";
}

const ICON_RULES=[
  {match:["subcategory"],     icon:FiLayers},
  {match:["category"],        icon:FiTag},
  {match:["company"],         icon:FiHome},
  {match:["supplier"],        icon:FiTruck},
  {match:["customer"],        icon:FiUsers},
  {match:["variant"],         icon:FiGrid},
  {match:["size"],            icon:FiSliders},
  {match:["colour","color"],  icon:FiDroplet},
  {match:["stock"],           icon:FiPackage},
  {match:["purchasedetails"], icon:FiFileText},
  {match:["purchase"],        icon:FiShoppingCart},
  {match:["salesdetails"],    icon:FiFileText},
  {match:["sales"],           icon:FiTrendingUp},
  {match:["payment"],         icon:FiCreditCard},
  {match:["product"],         icon:FiBox},
  {match:["barcode"],         icon:FiDatabase},
  {match:["employee"],        icon:FiUsers},
];

const MENU_ICON_MAP={
  dashboard: FiBarChart2,
  master:    FiGrid,
  reports:   FiPieChart,
};

function getIcon(name=""){
  const n=name.toLowerCase().replace(/\s+/g,"");
  for(const rule of ICON_RULES) if(rule.match.some(k=>n.includes(k))) return rule.icon;
  return FiFileText;
}

function getMenuIcon(name=""){
  return MENU_ICON_MAP[name.toLowerCase()] || FiFileText;
}

function buildGroups(submenus,query){
  const q=query.toLowerCase();
  const filtered=submenus.filter(s=>s.submenu_name.toLowerCase().includes(q));
  return GROUP_ORDER
    .map(key=>({ key, label:GROUP_LABELS[key], items:filtered.filter(s=>getGroupKey(s.submenu_name)===key) }))
    .filter(g=>g.items.length>0);
}

const RECENT_KEY="ys_recent_masters";
function getRecent(){ try{return JSON.parse(localStorage.getItem(RECENT_KEY))||[];}catch{return [];} }
function pushRecent(filename,name){
  const list=getRecent().filter(r=>r.filename!==filename);
  list.unshift({filename,name});
  localStorage.setItem(RECENT_KEY,JSON.stringify(list.slice(0,5)));
}

function greeting(){
  const h=new Date().getHours();
  if(h<12) return "Good morning";
  if(h<17) return "Good afternoon";
  return "Good evening";
}

function getUser(){
  try{return JSON.parse(localStorage.getItem("user"))||{};}catch{return {};}
}

function fmtNum(n){ return new Intl.NumberFormat("en-IN").format(n||0); }
function fmtAmt(n){ return new Intl.NumberFormat("en-IN",{style:"currency",currency:"INR",maximumFractionDigits:0}).format(n||0); }

/* ═══ KPI Card ══════════════════════════════════════════════════════════ */
function KpiCard({icon:Icon, value, label, desc, bg, color}){
  return(
    <div className="hub-kpi-card">
      <div className="hub-kpi-icon" style={{background:bg,color}}><Icon size={18}/></div>
      <div className="hub-kpi-body">
        <div className="hub-kpi-value">{value}</div>
        <div className="hub-kpi-label">{label}</div>
        <div className="hub-kpi-desc">{desc}</div>
      </div>
    </div>
  );
}

/* ═══ MasterHub ═════════════════════════════════════════════════════════ */
function MasterHub({submenus, onSelect}){
  const [query, setQuery] = useState("");
  const [recent, setRecent] = useState(getRecent);
  const [kpi, setKpi] = useState({ todaySales:0, totalProducts:0, totalCustomers:0, totalSuppliers:0 });
  const groups = buildGroups(submenus, query);

  useEffect(()=>{
    // Try to get some live KPIs from dashboard API
    const companyId = (() => { try{ return JSON.parse(localStorage.getItem("user"))?.fk_companyid||2; }catch{return 1;} })();
    api.get(`/Dashboard/details?companyid=${companyId}`)
      .then(res=>{
        const d=res.data;
        const todaySales=d[2]?.[0]?.Today_SalesAmount||0;
        setKpi({ todaySales });
      })
      .catch(()=>{});
  },[]);

  useEffect(()=>{
    const handler=(e)=>{
      if((e.ctrlKey||e.metaKey)&&e.key==="k"){
        e.preventDefault();
        document.getElementById("hub-search-input")?.focus();
      }
    };
    window.addEventListener("keydown",handler);
    return ()=>window.removeEventListener("keydown",handler);
  },[]);

  const handleSelect=(filename,name)=>{
    pushRecent(filename,name);
    setRecent(getRecent());
    onSelect(filename);
  };

  const totalMasters=submenus.length;
  const coreCount=submenus.filter(s=>getGroupKey(s.submenu_name)==="core").length;
  const prodCount=submenus.filter(s=>getGroupKey(s.submenu_name)==="product").length;
  const txnCount=submenus.filter(s=>getGroupKey(s.submenu_name)==="transactions").length;

  return(
    <div>
      {/* KPI row */}
      <div className="hub-kpi-grid">
        <KpiCard icon={FiDatabase}     value={totalMasters} label="Total Masters"   desc="All master records"  bg="#EFF6FF" color="#3B82F6"/>
        <KpiCard icon={FiBox}          value={prodCount}    label="Product modules" desc="Setup modules"       bg="#ECFDF5" color="#10B981"/>
        <KpiCard icon={FiUsers}        value={coreCount}    label="Core modules"    desc="Company, supplier…"  bg="#F0FDF4" color="#16A34A"/>
        <KpiCard icon={FiShoppingBag}  value={txnCount}     label="Transactions"    desc="Purchase, sales…"    bg="#FFFBEB" color="#F59E0B"/>
        <KpiCard icon={FiTrendingUp}   value={submenus.filter(s=>getGroupKey(s.submenu_name)==="other").length} label="Other modules" desc="Barcode, employee…" bg="#F8FAFC" color="#6B7280"/>
        <KpiCard icon={FiCreditCard}   value={fmtAmt(kpi.todaySales)} label="Today's Sales" desc="Total sales today" bg="#FFF1F2" color="#EF4444"/>
      </div>

      {/* Recently used */}
      {recent.length>0&&(
        <div className="hub-recent">
          <div className="hub-recent-label"><FiClock size={11}/> Recently used</div>
          <div className="hub-recent-list">
            {recent.map(r=>(
              <div key={r.filename} className="hub-recent-chip" onClick={()=>handleSelect(r.filename,r.name)}>
                <FiClock size={11}/> {r.name}
              </div>
            ))}
          </div>
        </div>
      )}

      {groups.length===0&&query&&<p className="hub-empty">No masters found for "{query}".</p>}

      {/* Group sections */}
      {groups.map(g=>(
        <div className="hub-section" key={g.key}>
          <div className="hub-section-header">
            <div className="hub-section-left">
              <div className={`hub-section-bar ${g.key}`}/>
              <span className="hub-section-title">{g.label}</span>
              <span className={`hub-section-count ${g.key}`}>{g.items.length} items</span>
            </div>
          </div>

          <div className="hub-modules-grid">
            {g.items.map(s=>{
              const Icon=getIcon(s.submenu_name);
              return(
                <div key={s.filename} className="hub-module-card" onClick={()=>handleSelect(s.filename,s.submenu_name)}>
                  <div className={`hub-module-icon ${g.key}`}><Icon size={18}/></div>
                  <div className="hub-module-footer">
                    <div>
                      <div className="hub-module-label">{s.submenu_name}</div>
                      <span className={`hub-module-badge ${g.key}`}>{g.label}</span>
                    </div>
                    <div className="hub-module-arrow"><FiArrowRight size={13}/></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ═══ MasterDetailHeader ════════════════════════════════════════════════ */
function MasterDetailHeader({submenus, activeFile, onBack, onSelect}){
  const [open, setOpen]=useState(false);
  const [query, setQuery]=useState("");
  const activeName=submenus.find(s=>s.filename===activeFile)?.submenu_name||"";
  const groups=buildGroups(submenus,query);

  return(
    <div className="master-detail-header">
      <button className="master-back-btn" onClick={onBack}>
        <FiArrowLeft size={13}/> All masters
      </button>
      <h2 className="master-detail-title">{activeName}</h2>
      <div style={{position:"relative"}}>
        <button className="switcher-btn" onClick={()=>setOpen(o=>!o)}>
          <span>Switch master</span><FiChevronDown size={13}/>
        </button>
        {open&&(
          <>
            <div className="switcher-backdrop" onClick={()=>setOpen(false)}/>
            <div className="switcher-panel">
              <input className="switcher-search" type="text" placeholder="Search master…" autoFocus
                value={query} onChange={e=>setQuery(e.target.value)}/>
              {groups.length===0&&<p className="hub-empty">No match.</p>}
              {groups.map(g=>(
                <div key={g.key}>
                  <div className="switcher-group-label">{g.label}</div>
                  {g.items.map(s=>{
                    const Icon=getIcon(s.submenu_name);
                    return(
                      <div key={s.filename}
                        className={s.filename===activeFile?"switcher-item active":"switcher-item"}
                        onClick={()=>{ onSelect(s.filename); pushRecent(s.filename,s.submenu_name); setOpen(false); setQuery(""); }}>
                        <Icon size={13}/> {s.submenu_name}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ═══ App Header ════════════════════════════════════════════════════════ */
function AppHeader({allSubmenus=[], onNavigate}){
  const user=getUser();
  const name=user.loginname||user.name||"Admin";
  const initial=(name[0]||"A").toUpperCase();
  const [searchQuery, setSearchQuery] = useState("");
  const [showDrop, setShowDrop]       = useState(false);
  const searchRef=useRef(null);
  const dropRef  =useRef(null);

  const filtered = searchQuery.trim().length > 0
    ? allSubmenus.filter(s =>
        s.submenu_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.menu_name.toLowerCase().includes(searchQuery.toLowerCase())
      ).slice(0, 8)
    : [];

  useEffect(()=>{
    const handler=(e)=>{
      if((e.ctrlKey||e.metaKey)&&e.key==="k"){ e.preventDefault(); searchRef.current?.focus(); setShowDrop(true); }
    };
    window.addEventListener("keydown",handler);
    return ()=>window.removeEventListener("keydown",handler);
  },[]);

  useEffect(()=>{
    const handler=(e)=>{ if(dropRef.current&&!dropRef.current.contains(e.target)) setShowDrop(false); };
    document.addEventListener("mousedown",handler);
    return ()=>document.removeEventListener("mousedown",handler);
  },[]);

  const handleSelect=(item)=>{
    onNavigate(item);
    setSearchQuery("");
    setShowDrop(false);
  };

  return(
    <header className="app-header">
      <div className="app-header-left">
        <p className="app-header-greeting">{greeting()}, {name} 👋</p>
        <p className="app-header-sub">Welcome to YashShop ERP</p>
      </div>

      <div className="app-header-search" ref={dropRef}>
        <span className="app-header-search-icon"><FiSearch size={15}/></span>
        <input
          ref={searchRef}
          type="text"
          placeholder="Search masters, transactions…"
          value={searchQuery}
          onChange={e=>{ setSearchQuery(e.target.value); setShowDrop(true); }}
          onFocus={()=>setShowDrop(true)}
        />
        {searchQuery
          ? <button className="app-search-clear" onClick={()=>{ setSearchQuery(""); setShowDrop(false); }}>✕</button>
          : <span className="app-search-kbd">Ctrl K</span>
        }

        {/* Search results dropdown */}
        {showDrop && filtered.length > 0 && (
          <div className="search-dropdown">
            {filtered.map((item,i)=>(
              <div key={i} className="search-result" onClick={()=>handleSelect(item)}>
                <span className="sr-name">{item.submenu_name}</span>
                <span className="sr-group">{item.menu_name}</span>
              </div>
            ))}
          </div>
        )}
        {showDrop && searchQuery.trim().length > 0 && filtered.length === 0 && (
          <div className="search-dropdown">
            <div className="sr-empty">No results for "{searchQuery}"</div>
          </div>
        )}
      </div>

      <div className="app-header-actions">
        <div className="app-header-user">
          <div className="app-user-avatar">{initial}</div>
          <span className="app-user-name">{name}</span>
          <FiChevronDown size={13} style={{color:"#94A3B8"}}/>
        </div>
      </div>
    </header>
  );
}

/* ═══ Layout root ═══════════════════════════════════════════════════════ */
function Layout(){
  const [menuData,setMenuData]             = useState([]);
  const [menu,setMenu]                     = useState("");
  const [submenu,setSubmenu]               = useState("");
  const [Component,setComponent]           = useState(null);
  const [autoLoaded,setAutoLoaded]         = useState(false);
  const [collapsed,setCollapsed]           = useState(false);   // desktop sidebar
  const [mobileSidebarOpen,setMobileSidebarOpen] = useState(false);

  const navigate=useNavigate();

  useEffect(()=>{
    const fetchMenu=async()=>{
      try{
        const res=await api.get("/Menu/getmenu");
        const menuMap={};
        res.data.forEach(item=>{
          if(!menuMap[item.menu_key])
            menuMap[item.menu_key]={menu_name:item.menu_name,menu_key:item.menu_key,submenus:[]};
          if(item.submenu_name&&item.filename)
            menuMap[item.menu_key].submenus.push({submenu_name:item.submenu_name,filename:item.filename});
        });
        setMenuData(Object.values(menuMap));
      }catch(err){console.error("Menu API Error:",err);}
    };
    fetchMenu();
  },[]);

  const handleLogout=()=>{ localStorage.clear(); navigate("/"); };

  const loadComponent=(file)=>{
    if(!file) return;
    const Dyn=lazy(()=>import(`./${file}.js`));
    setSubmenu(file);
    setComponent(()=>Dyn);
  };

  // Auto-load Dashboard on first login
  useEffect(()=>{
    if(autoLoaded||menuData.length===0) return;
    const dash=menuData.find(m=>
      m.menu_key?.toLowerCase()==="dashboard"||m.menu_name?.toLowerCase()==="dashboard"
    );
    if(dash){ setMenu(dash.menu_key); loadComponent(dash.submenus?.[0]?.filename||"Dashboard"); }
    setAutoLoaded(true);
  },[menuData,autoLoaded]);

  const currentMenuEntry = menuData.find(m=>m.menu_key===menu);
  const activeMenuName   = currentMenuEntry?.menu_name||"";
  const isMasterMenu     = currentMenuEntry?.menu_name?.toLowerCase()==="master"
                         ||currentMenuEntry?.menu_key?.toLowerCase()==="master";

  // All submenus flattened — for header search
  const allSubmenus = menuData.flatMap(m=>m.submenus.map(s=>({...s,menu_name:m.menu_name,menu_key:m.menu_key})));

  // Navigate to a submenu item (used by search + Quick Actions)
  const navigateTo = (item) => {
    setMenu(item.menu_key);
    loadComponent(item.filename);
  };

  // Quick Actions custom event listener
  useEffect(()=>{
    const handler=(e)=>{
      const file=e.detail?.file||e.detail?.filename;
      if(!file) return;
      const target=allSubmenus.find(s=>s.filename===file||s.submenu_name===file);
      if(target){ setMenu(target.menu_key); loadComponent(target.filename); }
    };
    window.addEventListener("ys-nav",handler);
    return ()=>window.removeEventListener("ys-nav",handler);
  },[menuData]);

  const handleSidebarClick=(m)=>{
    const isMaster=m.menu_name?.toLowerCase()==="master"||m.menu_key?.toLowerCase()==="master";
    setMenu(m.menu_key);
    setMobileSidebarOpen(false);
    if(isMaster){ setSubmenu(""); setComponent(null); }
    else if(m.submenus?.length>0){ loadComponent(m.submenus[0].filename); }
    else{ setSubmenu(""); setComponent(null); }
  };

  return(
    <div className="layout-container">

      {mobileSidebarOpen&&<div className="sidebar-backdrop" onClick={()=>setMobileSidebarOpen(false)}/>}

      {/* ── Sidebar ── */}
      <div className={`sidebar${collapsed?" collapsed":""}${mobileSidebarOpen?" open":""}`}>

        <div className="sidebar-logo">
          <img src={logo} alt="YashShop" className="sidebar-logo-img"/>
          <div className="sidebar-logo-text">
            <span className="sidebar-logo-name">YashShop</span>
            <span className="sidebar-logo-tagline">ERP System</span>
          </div>
          <button className="sidebar-collapse-btn" onClick={()=>setCollapsed(c=>!c)}
            aria-label={collapsed?"Expand sidebar":"Collapse sidebar"}>
            {collapsed?"»":"«"}
          </button>
        </div>

        <p className="menu-title">Navigation</p>

        <div className="sidebar-nav">
          {menuData.map(m=>{
            const Icon=getMenuIcon(m.menu_name);
            return(
              <button key={m.menu_key}
                className={`sidebar-nav-btn${menu===m.menu_key?" active":""}`}
                onClick={()=>handleSidebarClick(m)}
                title={collapsed?m.menu_name:undefined}>
                <span className="nav-icon"><Icon size={16}/></span>
                <span className="nav-label">{m.menu_name}</span>
              </button>
            );
          })}
        </div>

        <div className="sidebar-bottom">
          <button className="logout-btn" onClick={handleLogout} title={collapsed?"Logout":undefined}>
            <span className="nav-icon"><FiLogOut size={16}/></span>
            <span className="logout-label">Logout</span>
          </button>
        </div>
      </div>

      {/* ── Content wrapper ── */}
      <div className="content-wrapper">

        {/* Mobile topbar */}
        <div className="mobile-topbar">
          <button className="mobile-topbar-btn" onClick={()=>setMobileSidebarOpen(o=>!o)} aria-label="Menu">
            {mobileSidebarOpen?<FiX/>:<FiMenu/>}
          </button>
          <img src={logo} alt="" className="mobile-topbar-logo"/>
          <span className="mobile-topbar-title">{activeMenuName||"YashShop"}</span>
        </div>

        {/* Sticky app header — desktop only */}
        <AppHeader activeMenuName={activeMenuName} allSubmenus={allSubmenus} onNavigate={navigateTo}/>

        {isMasterMenu?(
          <div className="page-content">
            {Component?(
              <>
                <MasterDetailHeader
                  submenus={currentMenuEntry.submenus}
                  activeFile={submenu}
                  onBack={()=>{ setComponent(null); setSubmenu(""); }}
                  onSelect={loadComponent}
                />
                <Suspense fallback={<div style={{padding:32,color:"#64748B"}}>Loading…</div>}>
                  <Component/>
                </Suspense>
              </>
            ):(
              <MasterHub submenus={currentMenuEntry?.submenus||[]} onSelect={loadComponent}/>
            )}
          </div>
        ):(
          <>
            {menu&&(
              <div className="page-header-bar">
                <div className="page-header-left">
                  <span className="page-header-crumb">YashShop</span>
                  <span className="page-header-sep">›</span>
                  <span className="page-header-title">{activeMenuName}</span>
                  {submenu&&currentMenuEntry?.submenus?.length>1&&(
                    <>
                      <span className="page-header-sep">›</span>
                      <span className="page-header-active">
                        {currentMenuEntry.submenus.find(s=>s.filename===submenu)?.submenu_name||""}
                      </span>
                    </>
                  )}
                </div>
                {currentMenuEntry?.submenus?.length>1&&(
                  <div className="page-header-tabs">
                    {currentMenuEntry.submenus.map((s,i)=>(
                      <button key={i}
                        className={submenu===s.filename?"ph-tab active":"ph-tab"}
                        onClick={()=>loadComponent(s.filename)}>
                        {s.submenu_name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="page-content">
              {Component&&(
                <Suspense fallback={<div style={{padding:32,color:"#64748B"}}>Loading…</div>}>
                  <Component/>
                </Suspense>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Layout;